/**
 * BetaPanelPage.jsx — v1.0.0 Gate 9
 *
 * Tester-facing beta home. Tier, missions, mission feedback, direct messages,
 * and opt-out. Mounted at /beta.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, Star, CheckCircle2, Clipboard, MessageSquare, X,
  Sparkles, ChevronRight, ChevronDown, HelpCircle,
} from "lucide-react";
import { API, axios } from "@/api";
import { useTheme } from "@/contexts/ThemeContext";
import { BottomNav } from "@/components/shared/Navigation";

// v1.0.0 — category-level "How to complete" walkthroughs. Every mission in a
// category shares a common anchor so testers always know where to start.
// Copy grounded in actual UI elements that currently exist (no invented UI).
const CATEGORY_GUIDES = {
  discovery: {
    label: "Discovery missions",
    steps: [
      "Start from the Home tab (default landing page).",
      "Use the top search bar for keyword missions, or tap category tiles on Discover.",
      "Tap the heart icon on any card to save — it appears on every business card.",
      "Open any card to reach the full detail page.",
    ],
    look_for: "Search responsiveness, whether filters stick, empty-state copy clarity, and any card that fails to open.",
  },
  birthday: {
    label: "Birthday missions",
    steps: [
      "Open the My Birthday tab (bottom nav).",
      "Confirm your birthday in Profile → Settings before trying countdown-dependent missions.",
      "Itinerary and Wrapped live inside My Birthday — scroll down.",
      "Post-birthday survey only appears 1–7 days after your birthday date.",
    ],
    look_for: "Whether the countdown feels accurate, if wanted-promo adds confirm visually, and any Wrapped content that seems off.",
  },
  contribution: {
    label: "Contribution missions",
    steps: [
      "Tap the Contribute button (bottom nav center) for submit flows.",
      "Report-update lives on each business detail page (pencil icon).",
      "Report-issue lives on each offer detail page (bug icon, +10 pts).",
      "Your submission history is at Profile → My Contributions.",
    ],
    look_for: "Whether submissions confirm, whether the photo upload works, and how quickly points appear after you submit.",
  },
  social: {
    label: "Social missions",
    steps: [
      "Referral code lives at Profile → Refer a Friend.",
      "Share buttons appear on every offer card and detail page.",
      "Community Picks live on the Discover tab.",
      "Note: Social Mode is a coming-soon placeholder — spotting it counts for its mission.",
    ],
    look_for: "Whether share sheets open cleanly on iOS/Android, and whether the referral code copies correctly.",
  },
  profile: {
    label: "Profile missions",
    steps: [
      "Everything lives under the Profile tab (bottom nav right).",
      "Dietary preferences and wait tolerance are under Settings.",
      "Tips & Guide and How to Earn Points have their own menu items.",
      "Send Feedback is always accessible here OR via the pulsing pill at the bottom right.",
    ],
    look_for: "Whether profile photo uploads, whether dietary filters persist across app restarts.",
  },
  gamification: {
    label: "Gamification missions",
    steps: [
      "Open the Activity tab (bottom nav) to see points, badges, tiers, and punch cards.",
      "Tier thresholds: Explorer (100pts), Regular (500pts), Local Expert (1.5k+).",
      "Badges appear automatically as you trigger their conditions — no claim step needed.",
      "The first-badge celebration appears as a one-time modal.",
    ],
    look_for: "Whether points update in real-time, whether tier badges unlock at the advertised thresholds.",
  },
  vendor: {
    label: "Vendor missions (only if you run a business)",
    steps: [
      "Skip these entirely if you're not a verified vendor — they're not expected of every tester.",
      "Vendor Mode is accessible from the Mode Switcher (top-right of the header when logged in as a vendor).",
      "All vendor surfaces live under /vendor/* routes.",
    ],
    look_for: "Onboarding clarity, whether vendor settings save reliably, and any confusion about the verified-vendor gate.",
  },
  meta: {
    label: "Meta missions",
    steps: [
      "Opt into beta from Profile → Beta Program (if not already in).",
      "Broadcasts and direct messages appear in-app at Home and on /beta.",
      "Complete-N missions auto-tick as you finish other missions — no extra action.",
    ],
    look_for: "Whether broadcasts dismiss cleanly, whether mission completion counters roll up accurately.",
  },
};

export default function BetaPanelPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [missions, setMissions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [messageOpen, setMessageOpen] = useState(null);
  const [expandedMission, setExpandedMission] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m, msgs] = await Promise.all([
        axios.get(`${API}/beta/my-status`),
        axios.get(`${API}/beta/missions`).catch(() => ({ data: { missions: [] } })),
        axios.get(`${API}/beta/messages`).catch(() => ({ data: { messages: [] } })),
      ]);
      setStatus(s.data);
      setMissions(m.data?.missions || []);
      setMessages(msgs.data?.messages || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleJoin = async () => {
    setJoining(true);
    try {
      const res = await axios.post(`${API}/beta/join`);
      if (res.data?.already_member) toast("You're already in beta");
      else if (res.data?.founding_tester) toast.success(`Founding Tester #${res.data.beta_count}!`);
      else if (res.data?.joined) toast.success("Welcome to beta");
      await load();
    } catch {
      toast.error("Couldn't join beta");
    } finally { setJoining(false); }
  };

  const handleLeave = async () => {
    if (!window.confirm("Leave beta program?")) return;
    try {
      await axios.post(`${API}/beta/leave`);
      toast("Left beta");
      await load();
    } catch { toast.error("Couldn't leave"); }
  };

  const handleCompleteMission = async (mid) => {
    try {
      const res = await axios.post(`${API}/beta/missions/${mid}/complete`);
      if (res.data?.status === "completed") toast.success(`+${res.data.points_awarded} points`);
      setFeedbackFor(mid);
      await load();
    } catch { toast.error("Couldn't complete"); }
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackFor || !feedbackText.trim()) return;
    setFeedbackSubmitting(true);
    try {
      await axios.post(`${API}/beta/missions/${feedbackFor}/feedback`, {
        text: feedbackText, category: "mission_feedback",
      });
      toast.success("Thanks — we read every word");
      setFeedbackFor(null); setFeedbackText("");
      await load();
    } catch { toast.error("Couldn't submit"); }
    finally { setFeedbackSubmitting(false); }
  };

  const openMessage = async (msg) => {
    setMessageOpen(msg);
    if (!msg.read_at) {
      try {
        await axios.post(`${API}/beta/messages/${msg.id}/read`);
        setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, read_at: new Date().toISOString() } : m));
      } catch {}
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: theme.pageBg }}>
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: theme.primary }} />
    </div>
  );

  const isBeta = status?.is_beta_tester;
  const tier = status?.tier;

  return (
    <div className="min-h-screen pb-24" style={{ background: theme.pageBg }} data-testid="beta-panel-page">
      {/* Header */}
      <div className="px-5 pt-6 pb-5" style={{ background: theme.headerGradient }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-xl bg-white/10" data-testid="beta-back-btn">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white" data-testid="beta-title">Beta Program</h1>
            <p className="text-white/70 text-xs mt-0.5">Help shape the next version.</p>
          </div>
          {tier === "founding_tester" && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-400/20 border border-amber-300/30" data-testid="beta-tier-chip">
              <Star className="w-3 h-3 text-amber-300" />
              <span className="text-[10px] font-bold text-amber-200">FOUNDING</span>
            </div>
          )}
        </div>
      </div>

      {!isBeta ? (
        <div className="px-4 py-8" data-testid="beta-join-screen">
          {/* Incentive card — matches Profile page treatment */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-5 border border-purple-100">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-purple-500" />
              <p className="text-sm font-bold text-purple-700">Join the Beta Program</p>
            </div>
            <ul className="text-xs text-slate-700 space-y-2 ml-1">
              <li className="flex items-start gap-2">
                <span className="text-purple-400 mt-0.5">&#x2022;</span>
                <span>First access to new features before everyone</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-400 mt-0.5">&#x2022;</span>
                <span><strong>2x points</strong> on every action while testing</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-400 mt-0.5">&#x2022;</span>
                <span>Permanent <strong>Beta Tester</strong> badge on your profile</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-400 mt-0.5">&#x2022;</span>
                <span>Help shape what millions of users will experience</span>
              </li>
            </ul>
          </div>
          <button
            onClick={handleJoin}
            disabled={joining}
            className="mt-4 w-full py-3 rounded-xl bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 transition disabled:opacity-50"
            data-testid="beta-join-btn"
          >
            {joining ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Join Beta Program"}
          </button>
        </div>
      ) : (
        <div className="px-4 pt-4 space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-2" data-testid="beta-stats">
            <StatCell label="Missions" value={status.missions_completed} />
            <StatCell label="Feedback" value={status.feedback_submitted} />
            <StatCell label="Multiplier" value={`${status.multiplier}x`} />
          </div>

          {/* Messages */}
          {messages.length > 0 && (
            <section className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm" data-testid="beta-messages-section">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-slate-800">Messages from the Founder</h3>
                {status.unread_direct_messages > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500 text-white">
                    {status.unread_direct_messages} new
                  </span>
                )}
              </div>
              <div className="space-y-1.5">
                {messages.slice(0, 5).map((m) => (
                  <button
                    key={m.id}
                    onClick={() => openMessage(m)}
                    className={`w-full flex items-center gap-2 p-2 rounded-xl text-left border ${
                      m.read_at ? "bg-slate-50 border-slate-100" : "bg-amber-50 border-amber-200"
                    }`}
                    data-testid={`beta-message-${m.id}`}
                  >
                    <MessageSquare className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <p className="flex-1 text-xs text-slate-700 truncate">{m.body}</p>
                    <span className="text-[10px] text-slate-400">
                      {new Date(m.sent_at).toLocaleDateString()}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Missions */}
          <section className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm" data-testid="beta-missions-section">
            <h3 className="text-sm font-bold text-slate-800 mb-3">Missions</h3>
            <div className="space-y-2">
              {missions.map((m) => {
                const guide = CATEGORY_GUIDES[m.category] || null;
                const isExpanded = expandedMission === m.mission_id;
                return (
                <div
                  key={m.mission_id}
                  className={`rounded-xl border ${
                    m.completed ? "bg-emerald-50 border-emerald-100" : "bg-white border-slate-100"
                  }`}
                  data-testid={`beta-mission-${m.mission_id}`}
                >
                  <div className="flex items-center gap-3 p-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    m.completed ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"
                  }`}>
                    {m.completed ? <CheckCircle2 className="w-4 h-4" /> : <Clipboard className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800">{m.title}</p>
                    <p className="text-[11px] text-slate-600 mt-0.5 leading-snug">{m.description}</p>
                    {guide && (
                      <button
                        onClick={() => setExpandedMission(isExpanded ? null : m.mission_id)}
                        className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-sky-600 hover:text-sky-700"
                        data-testid={`beta-mission-howto-toggle-${m.mission_id}`}
                      >
                        <HelpCircle className="w-3 h-3" />
                        {isExpanded ? "Hide" : "How to complete"}
                        {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      </button>
                    )}
                  </div>
                  {!m.completed ? (
                    <button
                      onClick={() => handleCompleteMission(m.mission_id)}
                      className="px-3 py-1.5 rounded-lg bg-slate-800 text-white text-[11px] font-bold flex-shrink-0"
                      data-testid={`beta-mission-complete-${m.mission_id}`}
                    >
                      Mark done
                    </button>
                  ) : !m.feedback_submitted ? (
                    <button
                      onClick={() => setFeedbackFor(m.mission_id)}
                      className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 text-[11px] font-bold flex-shrink-0"
                      data-testid={`beta-mission-feedback-${m.mission_id}`}
                    >
                      Give feedback
                    </button>
                  ) : (
                    <span className="text-[10px] font-bold text-emerald-600">+{m.points}</span>
                  )}
                  </div>
                  {isExpanded && guide && (
                    <div className="px-3 pb-3 pt-1 border-t border-slate-100" data-testid={`beta-mission-howto-body-${m.mission_id}`}>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-2 mb-1.5">
                        {guide.label} — steps
                      </p>
                      <ol className="list-decimal list-inside space-y-1 text-[11px] text-slate-700 leading-snug">
                        {guide.steps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ol>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-3 mb-1">
                        What to look for
                      </p>
                      <p className="text-[11px] text-slate-600 italic leading-snug">{guide.look_for}</p>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </section>

          <button
            onClick={handleLeave}
            className="w-full py-3 rounded-xl bg-slate-100 text-slate-500 text-xs font-semibold"
            data-testid="beta-leave-btn"
          >
            Leave beta program
          </button>
        </div>
      )}

      {/* Feedback modal */}
      {feedbackFor && (
        <div className="fixed inset-0 z-[120] flex items-end" data-testid="beta-feedback-sheet">
          <div className="absolute inset-0 bg-black/40" onClick={() => setFeedbackFor(null)} />
          <motion.div
            initial={{ y: "100%" }} animate={{ y: 0 }}
            className="relative w-full max-w-md mx-auto bg-white rounded-t-3xl p-5"
          >
            <h3 className="text-sm font-bold text-slate-800">How did it go?</h3>
            <p className="text-[10px] text-slate-500 mt-0.5">Optional — +30 pts if you share. Admin reads every word.</p>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value.slice(0, 600))}
              placeholder="What worked, what didn't, any bugs?"
              rows={4}
              maxLength={600}
              className="mt-3 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-sky-200"
              data-testid="beta-feedback-textarea"
            />
            <p className="text-[10px] text-slate-400 mt-1 text-right">{feedbackText.length}/600</p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setFeedbackFor(null)}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 text-sm font-semibold"
                data-testid="beta-feedback-skip-btn"
              >
                Skip
              </button>
              <button
                onClick={handleSubmitFeedback}
                disabled={!feedbackText.trim() || feedbackSubmitting}
                className="flex-1 py-3 rounded-xl bg-sky-500 text-white text-sm font-bold disabled:opacity-50"
                data-testid="beta-feedback-submit-btn"
              >
                {feedbackSubmitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Submit"}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Direct message thread */}
      {messageOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" onClick={() => setMessageOpen(null)}>
          <div className="absolute inset-0 bg-black/50" />
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="relative bg-white rounded-3xl p-6 max-w-xs w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="beta-message-modal"
          >
            <button onClick={() => setMessageOpen(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
              <X className="w-4 h-4 text-slate-500" />
            </button>
            <p className="text-[10px] font-bold tracking-widest uppercase text-amber-600 mb-1">From the Founder</p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{messageOpen.body}</p>
            <p className="text-[10px] text-slate-400 mt-3">{new Date(messageOpen.sent_at).toLocaleString()}</p>
          </motion.div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

function StatCell({ label, value }) {
  return (
    <div className="bg-white rounded-xl p-3 text-center border border-slate-100">
      <p className="text-lg font-black text-slate-800">{value}</p>
      <p className="text-[10px] text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}
